import { createFileRoute } from '@tanstack/react-router';
import _ from 'lodash';
import TablerIconArrowRight from '~icons/tabler/arrow-right';
import TablerIconRocket from '~icons/tabler/rocket';

import { Grid, Stack, Typography } from 'gooey';
import { GlowCard } from '#/components/design';
import { Img } from '#/components/images';
import { ButtonLink } from '#/components/links';
import { Page, PageSection, PageSectionTitle } from '#/components/pages';
import { CustomerLogoList, FrequentlyAskedQuestions } from '#/components/widgets';
import fx from '#/fx';
import { Content, pageSEO } from '#/lib/content';
import { canonicalLinkTag } from '#/lib/head';

export const Route = createFileRoute('/_main/security')({
  loader: async () => {
    const page = await load();
    return { page };
  },
  head: ({ loaderData, match }) => ({
    meta: pageSEO(
      {
        title: 'Security',
        description: 'Learn about how Joggr keeps your data safe and secure.',
        keywords: ['security', 'privacy', 'compliance'],
      },
      match,
      loaderData?.page
    ),
    links: [canonicalLinkTag(match.pathname)],
  }),
  component: SecurityPage,
});

async function load() {
  const page = await fx.getPageContent({
    data: {
      key: 'security',
    },
  });

  const customerLogos = await fx.listCustomerLogos({
    data: {
      fields: {
        ids: page.sections.customers.logos,
      },
    },
  });

  return {
    ...page,
    sections: {
      ...page.sections,
      customers: {
        ...page.sections.customers,
        logos: customerLogos,
      },
    },
  };
}

type LoaderData = Awaited<ReturnType<typeof load>>;

function SecurityPage() {
  const { page } = Route.useLoaderData();
  return (
    <Page>
      <PageSectionHero sections={page.sections} />
      <PageSectionPrograms sections={page.sections} />
      <PageSectionDisclosure sections={page.sections} />
      <PageSectionCustomers sections={page.sections} />
      <PageSectionFAQ sections={page.sections} />
      <PageSectionCallToAction sections={page.sections} />
    </Page>
  );
}

function PageSectionHero({ sections }: SecurityPageSectionProps) {
  return (
    <PageSection className='text-center'>
      <div className='relative w-fit mx-auto mb-6'>
        <Img
          src='/brand/logo-icon-security.png'
          height={48}
          width={48}
          alt='Security Icon'
          className='max-w-[100px] sm:max-w-[124px] md:max-w-[184px] glow-2xl glow-primary rounded-full'
          responsive
        />
      </div>
      <Typography variant='h1' className='mb-2'>
        {sections.hero.title}
      </Typography>
      <Typography variant='h5' component='p' className='max-w-[800px] mx-auto text-gray-50'>
        {sections.hero.subtitle}
      </Typography>
    </PageSection>
  );
}

function PageSectionPrograms({ sections }: SecurityPageSectionProps) {
  const { programs } = sections;
  return (
    <PageSection gutterTop={0}>
      <Grid container spacing={3} rowSpacing={3} className='items-stretch justify-stretch'>
        {programs.map((program) => (
          <Grid
            size={{
              md: 4,
              sm: 6,
              xs: 12,
            }}
            key={program.title}
            className='self-stretch'
          >
            <GlowCard
              className='text-left h-full self-stretch cursor-pointer hover:glow-md glow-primary'
              gutter='xs'
            >
              <Stack spacing={1} direction='row' alignItems='center' className='mb-4'>
                <Img
                  location='static'
                  height={program.image.height}
                  width={program.image.width}
                  alt='Security Program'
                  className='max-w-[48px]'
                  src={program.image.src}
                />
                <Typography variant='h5' className='ml-2'>
                  {program.title}
                </Typography>
              </Stack>
              <Typography>
                <Content content={program.description} />
              </Typography>
            </GlowCard>
          </Grid>
        ))}
      </Grid>
    </PageSection>
  );
}

function PageSectionDisclosure({ sections }: SecurityPageSectionProps) {
  return (
    <PageSection className='text-center'>
      <div>
        <Typography variant='h3' className='mb-4'>
          <Content content={sections.disclosure.title} />
        </Typography>
        <Typography className='max-w-[800px] mx-auto'>
          <Content content={sections.disclosure.subtitle} />
        </Typography>
        <ButtonLink
          variant='contained'
          color='primary'
          className='mt-6'
          href={sections.disclosure.cta.url}
          target='_blank'
          size='large'
          rel='noopener noreferrer'
          endIcon={<TablerIconArrowRight />}
        >
          {sections.disclosure.cta.label}
        </ButtonLink>
      </div>
    </PageSection>
  );
}

function PageSectionCustomers({ sections }: SecurityPageSectionProps) {
  return (
    <PageSection className='text-center'>
      <GlowCard border='top'>
        <Stack spacing={6} justifyContent='center' alignItems='center'>
          <Typography variant='h5' className='text-gray-400'>
            {sections.customers.title}
          </Typography>
          <CustomerLogoList customerLogos={sections.customers.logos} opacity={1} />
        </Stack>
      </GlowCard>
    </PageSection>
  );
}

function PageSectionFAQ({ sections }: SecurityPageSectionProps) {
  return (
    <PageSection>
      <Stack spacing={4}>
        <Stack spacing={1}>
          <Typography variant='h2' className='mb-4'>
            {sections.faq.title}
          </Typography>
          <Typography variant='subtitle1'>
            <Content content={sections.faq.subtitle} />
          </Typography>
        </Stack>
        <FrequentlyAskedQuestions questions={sections.faq.questions} />
      </Stack>
    </PageSection>
  );
}

function PageSectionCallToAction({ sections }: SecurityPageSectionProps) {
  return (
    <PageSection className='text-center'>
      <GlowCard border='top'>
        <PageSectionTitle
          className='mb-4'
          icon={<TablerIconRocket />}
          iconAnimation='shake'
          title={sections.callToAction.title}
          subtitle={sections.callToAction.subtitle}
        />
        <ButtonLink
          variant='contained'
          to={sections.callToAction.cta.url}
          size='xlarge'
          className='w-fit mx-auto'
        >
          {sections.callToAction.cta.label}
        </ButtonLink>
      </GlowCard>
    </PageSection>
  );
}

/*
|------------------
| Internals
|------------------
*/

type SecurityPageSectionProps = Pick<LoaderData, 'sections'>;
